import { LEAGUE_TIERS, type PublicProfileDto } from '@spin/shared';
import { useEffect, useState } from 'react';
import { clientId, http } from '../api/http';
import { Dialog } from '../components/Dialog';
import { Avatar, Spinner } from '../components/common';
import { Emoji } from '../components/Emoji';
import { PriceHeart } from '../components/icons';
import { reportError, useT } from '../i18n';
import { useSession } from '../store/session';
import { useUi } from '../store/ui';
import { tg } from '../tg/bridge';

/** A player's card (the profile icon of "Send a gift to"): stats, admirer, friendship, block and report. */
export function ProfileDialog({ userId }: { userId: string }) {
  const t = useT();
  const me = useSession((s) => s.me)!;
  const { openDialog, toast } = useUi.getState();
  const [profile, setProfile] = useState<PublicProfileDto | null>(null);
  const [busy, setBusy] = useState(false);
  const isMe = userId === me.id;

  useEffect(() => {
    http.get<PublicProfileDto>(`/users/${userId}`).then(setProfile).catch(reportError);
  }, [userId]);

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    try {
      await action();
    } catch (err) {
      reportError(err);
    } finally {
      setBusy(false);
    }
  };

  if (!profile) {
    return (
      <Dialog centered testId="profile">
        <Spinner />
      </Dialog>
    );
  }
  const price = (profile.admirer?.price ?? 0) + 1;
  const tier = LEAGUE_TIERS[profile.leagueTier] ?? 'wooden';

  const admire = () =>
    run(async () => {
      if (price > me.balance) {
        openDialog('shop');
        return;
      }
      await http.post(`/users/${userId}/admire`, { price, clientTxId: clientId() });
      tg.haptic('success');
      toast(t('notify.admired', { name: me.displayName, price }), 'success');
      setProfile(await http.get<PublicProfileDto>(`/users/${userId}`));
    });
  const turnDown = () =>
    run(async () => {
      await http.post('/me/admirer/turn-down');
      toast(t('player.turned_down', { name: profile.admirer?.displayName ?? '' }));
      setProfile({ ...profile, admirer: null });
    });
  const toggleBlock = () =>
    run(async () => {
      if (profile.blockedByMe) await http.delete(`/users/${userId}/block`);
      else await http.post(`/users/${userId}/block`);
      setProfile({ ...profile, blockedByMe: !profile.blockedByMe, relation: 'none' });
    });
  const friend = () =>
    run(async () => {
      const res =
        profile.relation === 'friends' || profile.relation === 'outgoing'
          ? await http.delete<{ relation: PublicProfileDto['relation'] }>(`/friends/${userId}`)
          : await http.post<{ relation: PublicProfileDto['relation'] }>(`/friends/${userId}`);
      setProfile({ ...profile, relation: res.relation });
    });

  const friendLabel = {
    none: t('friends.add'),
    incoming: t('friends.accept'),
    outgoing: t('friends.requested'),
    friends: t('friends.remove'),
  }[profile.relation];

  return (
    <Dialog centered className="profile-dialog" testId="profile">
      <div className="profile-head">
        <span className="profile-photo">
          <Avatar name={profile.displayName} photoUrl={profile.photoUrl} size="100%" />
        </span>
        <div className="profile-info">
          <b className={profile.vip ? 'vip-name' : undefined}>
            {profile.displayName}
            {profile.age ? `, ${profile.age}` : ''}
          </b>
          <span>
            <Emoji e="💋" /> {profile.kissesReceived} · <Emoji e="🎁" /> {profile.giftsReceived} · ★{' '}
            {profile.achievements}
          </span>
          <span>
            {t(`league.tier.${tier}`)}
            {profile.friendsCount !== null && ` · ${t('friends.count', { count: profile.friendsCount })}`}
          </span>
        </div>
      </div>
      {profile.top.length > 0 && (
        <div className="top-badges" data-testid="top-badges">
          {profile.top.map((b) => (
            <span key={b} className="top-badge">
              TOP · {t(`ratings.${b}`)}
            </span>
          ))}
        </div>
      )}
      {profile.recentGifts.length > 0 && (
        <div className="recent-gifts">
          {profile.recentGifts.map((g, i) => (
            <span key={i} title={g.fromName}>
              <Emoji e={g.emoji} />
            </span>
          ))}
        </div>
      )}
      <p className="profile-admirer">
        {profile.admirer
          ? t('player.admirer', { name: profile.admirer.displayName, price: profile.admirer.price })
          : t('player.no_admirer')}
      </p>
      {isMe ? (
        profile.admirer && (
          <button
            className="btn btn-block"
            disabled={busy}
            onClick={() => void turnDown()}
            data-testid="turn-down"
          >
            {t('player.turn_down')}
          </button>
        )
      ) : (
        <div className="profile-actions">
          <button
            className="btn btn-green btn-block"
            disabled={busy || profile.blockedByMe || profile.admirer?.userId === me.id}
            onClick={() => void admire()}
            data-testid="admire"
          >
            {t('player.admire_for')} <PriceHeart /> {price}
          </button>
          <button
            className="btn btn-blue btn-block"
            disabled={busy || profile.blockedByMe}
            onClick={() => void friend()}
            data-testid="friend-toggle"
          >
            {friendLabel}
          </button>
          <div className="row">
            <button className="btn btn-block" disabled={busy} onClick={() => void toggleBlock()}>
              {profile.blockedByMe ? t('player.unblock') : t('player.block')}
            </button>
            <button
              className="btn btn-block"
              onClick={() => openDialog('report', { userId }, { stack: true })}
              data-testid="report"
            >
              {t('player.report')}
            </button>
          </div>
        </div>
      )}
    </Dialog>
  );
}

const REASONS = ['SPAM', 'ABUSE', 'SEXUAL_CONTENT', 'UNDERAGE', 'OTHER'] as const;

export function ReportDialog({ userId }: { userId: string }) {
  const t = useT();
  const report = async (reason: (typeof REASONS)[number]) => {
    try {
      await http.post(`/users/${userId}/report`, { reason });
      useUi.getState().toast(t('player.reported'), 'success');
      useUi.getState().closeDialog();
    } catch (err) {
      reportError(err);
    }
  };
  return (
    <Dialog title={t('player.report')} centered testId="report-dialog">
      <div className="report-list">
        {REASONS.map((r) => (
          <button key={r} className="btn btn-block" onClick={() => void report(r)}>
            {t(`report.${r}`)}
          </button>
        ))}
      </div>
    </Dialog>
  );
}
